import { useQuery } from '@tanstack/react-query'
import { axiosPrivate } from '@/shared/api/axios'
import { API } from '@/shared/constants/api'
import { queryKeys } from '@/shared/constants/query-keys'

interface UserRef {
  _id: string
  fullname: string
  avatar?: string | null
}

/** Management fields shown in the client side-panel (from GET /clients/:id). */
export interface IClientDetail {
  _id: string
  /**
   * El `cli_` del cliente — el ÚNICO id que entienden los endpoints v2.
   *
   * v1 ya lo devolvía en el cuerpo; lo que faltaba era mapearlo acá. Sin él, una pantalla que llega
   * con el `_id` en la URL —pasa cuando el FEIC se monta por la carga legacy, ver
   * `clients/utils/client-route-id.ts`— no tiene con qué preguntarle nada a v2.
   */
  publicId: string | null
  code: number
  typePerson: 'individual' | 'juridical'
  company: string | null
  agency: string | null
  advisor: UserRef | null
  createdBy: UserRef | null
  updatedBy: UserRef | null
  createdAt: string | null
  updatedAt: string | null
  /** Nombre legible del cliente (fullname individual / razón social jurídica). */
  name: string | null
  /** Correo del cliente (del applicant). Necesario para crear/enviar el flujo del portal. */
  email: string | null
}

interface ClientDetailResponse {
  client?: {
    _id: string
    publicId?: string | null
    code: number
    typePerson: 'individual' | 'juridical'
    company?: string | null
    agency?: string | null
    advisor?: UserRef | null
    createdBy?: UserRef | null
    updatedBy?: UserRef | null
    createdAt?: string | null
    updatedAt?: string | null
  }
  // GET /clients/:id mezcla el person-* al top level: applicant (individual/jurídica).
  applicant?: {
    fullname?: string | null
    companyName?: string | null
    // Individual: string. Jurídica: la empresa guarda el correo como arreglo — se
    // normaliza al exponerlo (el contrato del detalle es un único string).
    email?: string | string[] | null
  }
}

/** Loads the client's management data for the detail side-panel. */
export function useClientDetailQuery(id: string, enabled = true) {
  return useQuery<IClientDetail | null>({
    queryKey: queryKeys.clients.detail(id),
    queryFn: async () => {
      const { data } = await axiosPrivate.get<ClientDetailResponse>(API.clients.byId(id))
      const c = data.client
      if (!c) return null
      const applicant = data.applicant
      return {
        _id: c._id,
        publicId: c.publicId ?? null,
        code: c.code,
        typePerson: c.typePerson,
        company: c.company ?? null,
        agency: c.agency ?? null,
        advisor: c.advisor ?? null,
        createdBy: c.createdBy ?? null,
        updatedBy: c.updatedBy ?? null,
        createdAt: c.createdAt ?? null,
        updatedAt: c.updatedAt ?? null,
        name: applicant?.fullname ?? applicant?.companyName ?? null,
        // Jurídica trae `email` como arreglo; individual como string. Se aplana al
        // primer correo para cumplir el contrato `email: string | null` (lo consume el
        // form de crear/enviar el flujo del portal, que espera un único destinatario).
        email: (Array.isArray(applicant?.email) ? applicant?.email[0] : applicant?.email) ?? null
      }
    },
    enabled: enabled && !!id,
    refetchOnWindowFocus: false
  })
}
